import React, { useState } from "react";
import styled from "styled-components";
import { FiArrowUpRight, FiCode, FiGlobe, FiGrid, FiSearch, FiX } from "react-icons/fi";
import SkillGlobe from "./SkillGlobe";
import { iconMap } from "./skillIcons";
import skills from "./skills.json";

const TechStack = () => {
  const [view, setView] = useState("globe");
  const [query, setQuery] = useState("");
  const filtered = skills.filter((skill) => skill.Skill.toLowerCase().includes(query.trim().toLowerCase()));
  const showList = view === "list" || query.trim().length > 0;

  return (
    <Container id="techstack" aria-labelledby="stack-heading">
      <Layout>
        <Introduction>
          <Eyebrow>The tools behind the work</Eyebrow>
          <h2 id="stack-heading">My stack.<br /><span>A world to explore.</span></h2>
          <p>From interfaces and APIs to databases and deployment. These are the languages, frameworks, and tools I use to bring ideas to life.</p>
          <Legend><span aria-hidden="true" /> Dark labels highlight my core skills.</Legend>
          <Search>
            <FiSearch aria-hidden="true" />
            <input type="search" aria-label="Find a technology" placeholder="Find a technology…" value={query} onChange={(event) => setQuery(event.target.value)} />
            {query && <button type="button" aria-label="Clear search" onClick={() => setQuery("")}><FiX aria-hidden="true" /></button>}
          </Search>
          <ViewSwitch role="group" aria-label="Skills view">
            <button type="button" aria-pressed={!showList} onClick={() => { setView("globe"); setQuery(""); }}><FiGlobe aria-hidden="true" /> Explore globe</button>
            <button type="button" aria-pressed={showList} onClick={() => setView("list")}><FiGrid aria-hidden="true" /> View all skills</button>
          </ViewSwitch>
          <Count role="status">{query.trim() ? `${filtered.length} of ${skills.length} technologies found` : `${skills.length} technologies to explore`}</Count>
          <ProjectLink href="#projects">See them in practice <FiArrowUpRight aria-hidden="true" /></ProjectLink>
        </Introduction>
        <Explorer aria-label="Technology explorer">
          {showList ? (
            <ListPanel>
              <ListHeading>{query.trim() ? "Search results" : "The complete toolkit"}</ListHeading>
              {filtered.length ? (
                <SkillList aria-label="Technologies">
                  {filtered.map((skill) => {
                    const Icon = iconMap[skill.Icon] || FiCode;
                    return <SkillItem key={skill.Skill} $core={skill.Underline}><Icon aria-hidden="true" /><span>{skill.Skill}</span>{skill.Underline && <ScreenReaderOnly> (core skill)</ScreenReaderOnly>}</SkillItem>;
                  })}
                </SkillList>
              ) : <Empty>No matching technologies. Try another name or clear the search.</Empty>}
            </ListPanel>
          ) : <SkillGlobe skills={skills} />}
        </Explorer>
      </Layout>
    </Container>
  );
};

export default TechStack;

const Container = styled.section`
  padding: 4rem 1.25rem;
  background: radial-gradient(ellipse at 90% 50%, #edf7f0 0%, transparent 60%), #fcfdfc;
  color: #2b2b35;
  @media (max-width: 599px) { padding: 3rem 1rem; }
  @media (min-width: 840px) { padding: 5.5rem 5%; }
`;
const Layout = styled.div`
  display: grid;
  gap: 1.5rem;
  align-items: center;
  max-width: 1120px;
  margin-inline: auto;
  grid-template-columns: minmax(0, 1fr);
  @media (max-width: 599px) { gap: 1rem; }
  @media (min-width: 960px) { grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr); gap: 2.5rem; }
`;
const Introduction = styled.div`
  min-width: 0;
  h2 { font-size: clamp(2rem, 4vw, 3.25rem); font-weight: 500; line-height: 1.15; letter-spacing: -0.055em; }
  h2 span { color: #008369; }
  > p:not(:first-child) { max-width: 42ch; margin-top: 1.25rem; color: #59615e; font-size: 0.9rem; line-height: 1.85; }
  @media (max-width: 599px) {
    text-align: center;
    h2 { font-size: clamp(1.85rem, 7.6vw, 2.5rem); }
    > p:not(:first-child) { margin: 1rem auto 0; font-size: 0.85rem; line-height: 1.75; }
  }
`;
const Eyebrow = styled.p`
  margin-bottom: 1.5rem; color: #476259; font-size: 0.65rem; font-weight: 600; letter-spacing: 0.13em; text-transform: uppercase;
`;
const Legend = styled.div`
  display: flex; align-items: center; gap: 0.6rem;
  margin-top: 1.5rem; font-size: 0.7rem; line-height: 1.6; color: #59615e;
  span { width: 9px; height: 9px; flex-shrink: 0; border-radius: 50%; background: #183c32; }
  @media (max-width: 599px) { justify-content: center; margin-top: 1rem; }
`;
const Search = styled.div`
  display: flex; align-items: center; gap: 0.6rem;
  margin-top: 1.75rem; padding: 0 0.85rem;
  border: 1px solid #cedfd3; border-radius: 0.85rem; background: #fff;
  color: #65736b;
  &:focus-within { outline: 2px solid #008369; outline-offset: 3px; }
  > svg { flex-shrink: 0; }
  input { width: 100%; min-width: 0; min-height: 48px; border: 0; outline: 0; background: transparent; color: #354b42; font-size: 1rem; }
  input::-webkit-search-cancel-button { -webkit-appearance: none; }
  button { display: grid; place-items: center; flex-shrink: 0; width: 44px; height: 44px; border: 0; background: none; color: #354b42; cursor: pointer; }
  button:focus-visible { outline: 2px solid #008369; border-radius: 4px; }
  @media (max-width: 599px) { margin-top: 1.25rem; }
`;
const ViewSwitch = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 1rem;
  button {
    display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem;
    min-height: 44px; padding: 0.65rem 0.9rem; border: 1px solid #d6e4db; border-radius: 999px;
    background: transparent; color: #47594f; font-size: 0.7rem; cursor: pointer;
    &[aria-pressed="true"] { color: #fff; background: #183c32; border-color: #183c32; }
    &:focus-visible { outline: 2px solid #008369; outline-offset: 3px; }
  }
  @media (max-width: 599px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    button { padding-inline: 0.5rem; font-size: 0.75rem; }
  }
`;
const Count = styled.div`margin-top: 0.9rem; color: #65736b; font-size: 0.7rem;`;
const ProjectLink = styled.a`
  display: inline-flex; align-items: center; gap: 0.75rem; min-height: 44px; margin-top: 1.25rem;
  color: #007b61; font-size: 0.8rem; font-weight: 600; text-decoration: none;
  &:hover { text-decoration: underline; text-underline-offset: 5px; }
  &:focus-visible { outline: 2px solid #008369; outline-offset: 3px; }
`;
const Explorer = styled.div`width: 100%; min-width: 0; max-width: 620px; margin-inline: auto;`;
const ListPanel = styled.div`
  padding: 1.25rem; border: 1px solid #d6e4db; border-radius: 1.5rem; background: #ffffffb3;
  @media (min-width: 960px) { min-height: 580px; }
  @media (max-width: 599px) { padding: 1rem; border-radius: 1rem; }
`;
const ListHeading = styled.h3`margin-bottom: 1.25rem; color: #354b42; font-size: 0.9rem; font-weight: 500;`;
const SkillList = styled.ul`
  display: flex; flex-wrap: wrap; gap: 0.5rem; list-style: none;
  @media (max-width: 599px) { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;
const SkillItem = styled.li`
  display: inline-flex; align-items: center; gap: 0.4rem;
  padding: 0.6rem 0.75rem; border: 1px solid ${({ $core }) => $core ? "#183c32" : "#d6e4db"}; border-radius: 999px;
  background: ${({ $core }) => $core ? "#183c32" : "#fff"}; color: ${({ $core }) => $core ? "#fff" : "#354b42"};
  font-size: 0.7rem; line-height: 1.5;
  svg { flex-shrink: 0; font-size: 1rem; }
  @media (max-width: 599px) {
    min-width: 0; min-height: 44px; padding: 0.5rem; border-radius: 0.6rem; font-size: 0.75rem;
    > span { min-width: 0; overflow-wrap: anywhere; }
  }
`;
const Empty = styled.p`color: #59615e; font-size: 0.85rem; line-height: 1.8;`;
const ScreenReaderOnly = styled.span`
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
`;
